import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { toast } from "react-toastify";

const formatMessageTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return `${date.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
  })}, ${date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};

/**
 * `direction` + `sentByRole` decide how a message is rendered:
 * Incoming  -> left  (customer)
 * Agent     -> right (you)
 * Agency    -> right (owner participated from the agency dashboard)
 * anything  -> right (system / automated reply)
 */
const getMessageMeta = (message) => {
  if (message.direction === "Incoming") {
    return { isIncoming: true, label: "Customer" };
  }

  if (message.sentByRole === "Agent") {
    return { isIncoming: false, label: "You" };
  }

  if (message.sentByRole === "Agency") {
    return { isIncoming: false, label: "Owner" };
  }

  return { isIncoming: false, label: "System" };
};

const MessageBubble = ({ message }) => {
  const { isIncoming, label } = getMessageMeta(message);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isIncoming ? "flex-start" : "flex-end",
        mb: 1.5,
      }}
    >
      <Box
        sx={(theme) => ({
          maxWidth: "72%",
          px: 2,
          py: 1,
          borderRadius: "12px",
          bgcolor: isIncoming ? theme.palette.grey[100] : theme.palette.primary.main,
          color: isIncoming ? "text.primary" : "white",
        })}
      >
        <Typography
          variant="caption"
          sx={{ display: "block", fontWeight: 600, opacity: 0.75 }}
        >
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
        >
          {message.message}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 0.5,
            opacity: 0.75,
            textAlign: isIncoming ? "left" : "right",
          }}
        >
          {formatMessageTime(message.createdAt)}
        </Typography>
      </Box>
    </Box>
  );
};

/**
 * Conversation view for the agent WhatsApp inbox.
 * `onSend` must return a promise so a failed send keeps the typed message.
 */
const MessageThread = ({
  title,
  subtitle,
  messages = [],
  isLoading,
  onSend,
  isSending,
  emptyText = "Select a conversation to see the messages.",
}) => {
  const [text, setText] = useState("");
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, title]);

  const handleSend = async () => {
    const message = text.trim();
    if (!message || isSending) return;

    try {
      await onSend(message);
      setText("");
    } catch (error) {
      toast.error(error?.data?.message || "Error Sending Message");
    }
  };

  if (!title) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 4,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {emptyText}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
      <Box sx={{ px: 2, py: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
      <Divider />

      <Box sx={{ flex: 1, overflowY: "auto", p: 2, bgcolor: "background.default" }}>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : messages.length ? (
          messages.map((message) => (
            <MessageBubble key={message.id} message={message} />
          ))
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center", py: 4 }}
          >
            No messages yet.
          </Typography>
        )}
        <div ref={bottomRef} />
      </Box>

      <Divider />
      <Box sx={{ display: "flex", gap: 1, p: 2 }}>
        <TextField
          fullWidth
          size="small"
          multiline
          maxRows={4}
          placeholder="Type a message"
          value={text}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              handleSend();
            }
          }}
        />
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={isSending || !text.trim()}
          sx={{ color: "white", minWidth: "auto", px: 2 }}
        >
          {isSending ? (
            <CircularProgress size={20} sx={{ color: "white" }} />
          ) : (
            <SendIcon fontSize="small" />
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default MessageThread;
